import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import CampaignIcon from "@mui/icons-material/Campaign";
import DashboardIcon from "@mui/icons-material/Dashboard";
import DescriptionIcon from "@mui/icons-material/Description";
import FacebookIcon from "@mui/icons-material/Facebook";
import GavelIcon from "@mui/icons-material/Gavel";
import GroupIcon from "@mui/icons-material/Group";
import InstagramIcon from "@mui/icons-material/Instagram";
import LanguageIcon from "@mui/icons-material/Language";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { QrCode } from "@mui/icons-material";
import XIcon from "@mui/icons-material/X";
import {
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  IconButton,
  Snackbar,
  TextField,
  Toolbar,
  Typography,
  useTheme,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import React, { useState, useEffect } from "react";
import Logo from "../assets/logo.svg";
import AnimatedBackground from "../components/AnimatedBackground";

const theme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#0C173A",
    },
    secondary: {
      main: "#00CED1",
    },
    background: {
      default: "#0C173A",
      paper: "#0B121C",
    },
    text: {
      primary: "#ffffff",
      secondary: "rgba(255, 255, 255, 0.85)",
    },
    action: {
      hover: "#00CED1",
    },
  },
  typography: {
    fontFamily: "'Roboto', sans-serif",
    allVariants: {
      color: "#ffffff",
    },
    h3: {
      fontSize: "4rem",
      letterSpacing: "-0.5px",
      fontWeight: 800,
    },
    h4: {
      fontSize: "3rem",
      letterSpacing: "-0.25px",
      fontWeight: 700,
    },
    h5: {
      fontSize: "2rem",
      letterSpacing: "-0.25px",
      fontWeight: 700,
    },
    h6: {
      fontSize: "1.5rem",
      lineHeight: 1.6,
      fontWeight: 600,
    },
    subtitle1: {
      fontSize: "1.3rem",
      lineHeight: 1.6,
    },
    body1: {
      fontSize: "1.1rem",
      lineHeight: 1.7,
    },
    body2: {
      fontSize: "1rem",
      lineHeight: 1.6,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: "14px 36px",
          textTransform: "none",
          fontSize: { xs: "1rem", md: "1.1rem" },
          fontWeight: 600,
          letterSpacing: "0.5px",
          transition: "all 0.2s ease-in-out",
          background: "linear-gradient(135deg, #00CED1 0%, #4169E1 100%)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          boxShadow: "0 4px 16px rgba(0, 206, 209, 0.15)",
          "&:hover": {
            background: "linear-gradient(135deg, #00CED1 20%, #4169E1 100%)",
            boxShadow: "0 6px 20px rgba(0, 206, 209, 0.25)",
            borderColor: "rgba(255, 255, 255, 0.2)",
          },
          "&:disabled": {
            background: "linear-gradient(135deg, #1E3050 0%, #0C173A 100%)",
            opacity: 0.7,
          },
        },
        contained: {
          "&.MuiButton-containedSecondary": {
            background: "linear-gradient(135deg, #0E2D54 0%, #275077 100%)",
            border: "1px solid rgba(0, 206, 209, 0.2)",
            "&:hover": {
              background: "linear-gradient(135deg, #0E2D54 20%, #275077 100%)",
            },
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          transition: "all 0.3s ease-in-out",
          backgroundColor: "rgba(11, 18, 28, 0.6)",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          "&:hover": {
            transform: "translateY(-8px)",
            backgroundColor: "rgba(14, 45, 84, 0.8)",
            boxShadow: "0 12px 32px rgba(0, 206, 209, 0.2)",
            border: "1px solid rgba(0, 206, 209, 0.3)",
            "& .MuiSvgIcon-root": {
              transform: "scale(1.1) rotate(5deg)",
              color: "#00CED1",
            },
            "& .card-title": {
              background: "linear-gradient(45deg, #00CED1, #4169E1)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            },
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: "#00CED1",
          transition: "all 0.2s",
          "&:hover": {
            transform: "translateY(-2px)",
            color: "#00CED1",
          },
        },
      },
    },
  },
});

const tools = [
  {
    title: "Scanmatiq",
    description:
      "A QR code generator that lets you create, customize, and track dynamic and static QR codes — with real-time analytics and smart link control.",
    icon: <QrCode fontSize="large" color="secondary" />,
  },
  {
    title: "Ragmatiq",
    description:
      "Upload any document and chat with it like a person. NLP-powered insights, summaries, and Q&A for PDFs, docs, and reports.",
    icon: <DescriptionIcon fontSize="large" color="secondary" />,
  },
  {
    title: "Teamatiq",
    description:
      "AI workforce management — understand behavior, predict burnout, and track productivity intelligently.",
    icon: <GroupIcon fontSize="large" color="secondary" />,
  },
  {
    title: "Relmatiq",
    description:
      "AI-enhanced CRM to score leads, automate outreach, and predict behavior for higher conversions.",
    icon: <TrendingUpIcon fontSize="large" color="secondary" />,
  },
  {
    title: "Buzzmatiq",
    description:
      "AI-powered social media dashboard — schedule, post, monitor, and analyze across all platforms.",
    icon: <DashboardIcon fontSize="large" color="secondary" />,
  },
  {
    title: "Chatmatiq",
    description:
      "Offer lightning-fast, 24/7 AI support that understands context and tone. Route issues smartly and learn from each interaction.",
    icon: <SupportAgentIcon fontSize="large" color="secondary" />,
  },
  {
    title: "Admatiq",
    description:
      "An AI-powered ad manager that gives you full control over placements, targeting, and campaign performance — all in one intuitive dashboard.",
    icon: <CampaignIcon fontSize="large" color="secondary" />,
  },
  {
    title: "ATSmatiq",
    description:
      "An AI-powered Applicant Tracking System that doesn't just track resumes — it analyzes, verifies, and ranks them using machine learning and smart matching.",
    icon: <AssignmentIndIcon fontSize="large" color="secondary" />,
  },
  {
    title: "Webmatiq",
    description:
      "Generate and deploy fully responsive websites from a single prompt — complete with modern layouts and live hosting.",
    icon: <LanguageIcon fontSize="large" color="secondary" />,
  },
  // {
  //   title: "Legalmatiq",
  //   description:
  //     "Create contracts, agreements, and legal docs instantly from simple prompts — accurate, customizable, and signature-ready.",
  //   icon: <GavelIcon fontSize="large" color="secondary" />,
  // },
];

function ResponsiveHeader() {
  const theme = useTheme();

  return (
    <AppBar
      position="fixed"
      color="primary"
      elevation={0}
      sx={{
        backgroundColor: "#0C173A",
      }}
    >
      <Container maxWidth="lg">
        <Toolbar sx={{ py: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
            <img src={Logo} alt="Bloomatiq" style={{ height: 40 }} />
          </Box>

          {/* <Button
            variant="contained"
            sx={{
              minWidth: { xs: "120px", sm: "140px" },
              fontSize: { xs: "1rem", sm: "1.1rem" },
            }}
          >
            Login
          </Button> */}
        </Toolbar>
      </Container>
    </AppBar>
  );
}

function FooterBackground() {
  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        overflow: "hidden",
        opacity: 0.7,
      }}
    >
      <svg
        style={{
          width: "100%",
          height: "100%",
          position: "absolute",
          top: 0,
          left: 0,
        }}
      >
        <defs>
          <linearGradient id="footerGrad1" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop
              offset="0%"
              style={{ stopColor: "#00CED1", stopOpacity: 0.2 }}
            />
            <stop
              offset="100%"
              style={{ stopColor: "#4169E1", stopOpacity: 0.05 }}
            />
          </linearGradient>
          <linearGradient id="footerGrad2" x1="100%" y1="0%" x2="0%" y2="100%">
            <stop
              offset="0%"
              style={{ stopColor: "#FF1493", stopOpacity: 0.15 }}
            />
            <stop
              offset="100%"
              style={{ stopColor: "#9370DB", stopOpacity: 0.05 }}
            />
          </linearGradient>
          <linearGradient id="footerGrad3" x1="0%" y1="100%" x2="100%" y2="0%">
            <stop
              offset="0%"
              style={{ stopColor: "#00FF00", stopOpacity: 0.1 }}
            />
            <stop
              offset="100%"
              style={{ stopColor: "#4169E1", stopOpacity: 0.05 }}
            />
          </linearGradient>
          <filter id="footerGlow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="footerNeonGlow">
            <feFlood result="flood" floodColor="#00CED1" floodOpacity=".3" />
            <feComposite
              in="flood"
              result="mask"
              in2="SourceGraphic"
              operator="in"
            />
            <feMorphology
              in="mask"
              result="dilated"
              operator="dilate"
              radius="1"
            />
            <feGaussianBlur in="dilated" result="blurred" stdDeviation="2" />
            <feMerge>
              <feMergeNode in="blurred" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <pattern
            id="circuit"
            x="0"
            y="0"
            width="50"
            height="50"
            patternUnits="userSpaceOnUse"
          >
            <path
              d="M 25 0 L 25 25 M 0 25 L 50 25"
              fill="none"
              stroke="rgba(0,255,255,0.1)"
              strokeWidth="0.5"
            />
            <circle cx="25" cy="25" r="2" fill="rgba(255,20,147,0.1)" />
          </pattern>
        </defs>

        {/* Circuit Background */}
        <rect width="100%" height="100%" fill="url(#circuit)" />

        {/* Data Streams */}
        {[...Array(10)].map((_, i) => (
          <path
            key={`stream-${i}`}
            d={`M ${i * 10} 0 L ${i * 10 + 50} 100`}
            stroke={i % 2 === 0 ? "url(#footerGrad1)" : "url(#footerGrad2)"}
            strokeWidth="0.5"
            strokeDasharray="4 4"
            filter="url(#footerNeonGlow)"
            style={{
              animation: `dataStream${i} ${5 + i * 0.5}s linear infinite`,
            }}
          />
        ))}

        {/* AI Nodes */}
        {[...Array(8)].map((_, i) => (
          <g key={`ai-node-${i}`} filter="url(#footerNeonGlow)">
            <circle
              r="4"
              fill={
                i % 3 === 0 ? "#00CED1" : i % 3 === 1 ? "#FF1493" : "#4169E1"
              }
              style={{
                transform: `translate(${20 + i * 10}%, 50%)`,
                animation: `aiNode${i} ${6 + i}s ease-in-out infinite`,
              }}
            />
            <path
              d={`M ${-8} 0 L ${8} 0 M 0 ${-8} L 0 ${8}`}
              stroke={
                i % 3 === 0 ? "#00CED1" : i % 3 === 1 ? "#FF1493" : "#4169E1"
              }
              strokeWidth="0.5"
              style={{
                transform: `translate(${20 + i * 10}%, 50%) rotate(${
                  45 * i
                }deg)`,
                animation: `aiNodeCross${i} ${6 + i}s ease-in-out infinite`,
              }}
            />
          </g>
        ))}

        {/* Quantum Circles */}
        {[...Array(5)].map((_, i) => (
          <g key={`quantum-${i}`}>
            <circle
              r={6 + i * 2}
              fill="none"
              stroke={i % 2 === 0 ? "url(#footerGrad1)" : "url(#footerGrad2)"}
              strokeWidth="0.5"
              filter="url(#footerNeonGlow)"
              style={{
                transform: `translate(${60 + i * 8}%, ${30 + i * 10}%)`,
                animation: `quantumPulse${i} ${8 + i}s ease-in-out infinite`,
              }}
            />
            <circle
              r={3}
              fill={
                i % 3 === 0 ? "#00CED1" : i % 3 === 1 ? "#FF1493" : "#4169E1"
              }
              filter="url(#footerNeonGlow)"
              style={{
                transform: `translate(${60 + i * 8}%, ${30 + i * 10}%)`,
                animation: `quantumCore${i} ${8 + i}s ease-in-out infinite`,
              }}
            />
          </g>
        ))}

        {/* Matrix Rain */}
        {[...Array(15)].map((_, i) => (
          <text
            key={`matrix-${i}`}
            x={`${85 + i}%`}
            y="10%"
            fill={i % 3 === 0 ? "#00CED1" : i % 3 === 1 ? "#FF1493" : "#4169E1"}
            fontSize="8"
            filter="url(#footerNeonGlow)"
            style={{
              animation: `matrixRain${i} ${3 + i * 0.2}s linear infinite`,
            }}
          >
            {[...Array(5)].map((_, j) => (
              <tspan key={j} x={`${85 + i}%`} dy="10">
                {Math.random().toString(16).substr(2, 1)}
              </tspan>
            ))}
          </text>
        ))}
      </svg>

      <style>
        {[...Array(10)]
          .map(
            (_, i) => `
          @keyframes dataStream${i} {
            0% { stroke-dashoffset: 0; }
            100% { stroke-dashoffset: -16; }
          }
        `
          )
          .join("\n")}

        {[...Array(8)]
          .map(
            (_, i) => `
          @keyframes aiNode${i} {
            0%, 100% { transform: translate(${
              20 + i * 10
            }%, 50%) scale(1); opacity: 0.5; }
            50% { transform: translate(${
              20 + i * 10
            }%, 48%) scale(1.3); opacity: 0.8; }
          }
          @keyframes aiNodeCross${i} {
            0%, 100% { transform: translate(${20 + i * 10}%, 50%) rotate(${
              45 * i
            }deg) scale(1); }
            50% { transform: translate(${20 + i * 10}%, 48%) rotate(${
              45 * i + 180
            }deg) scale(1.2); }
          }
        `
          )
          .join("\n")}

        {[...Array(5)]
          .map(
            (_, i) => `
          @keyframes quantumPulse${i} {
            0%, 100% { transform: translate(${60 + i * 8}%, ${
              30 + i * 10
            }%) scale(1); opacity: 0.3; }
            50% { transform: translate(${60 + i * 8}%, ${
              30 + i * 10
            }%) scale(1.5); opacity: 0.6; }
          }
          @keyframes quantumCore${i} {
            0%, 100% { transform: translate(${60 + i * 8}%, ${
              30 + i * 10
            }%) scale(1); }
            50% { transform: translate(${60 + i * 8}%, ${
              28 + i * 10
            }%) scale(0.8); }
          }
        `
          )
          .join("\n")}

        {[...Array(15)]
          .map(
            (_, i) => `
          @keyframes matrixRain${i} {
            0% { transform: translateY(-50px); opacity: 0; }
            10% { opacity: 0.5; }
            90% { opacity: 0.5; }
            100% { transform: translateY(200px); opacity: 0; }
          }
        `
          )
          .join("\n")}
      </style>
    </Box>
  );
}

export default function Landing() {
  const waitlistRef = React.useRef(null);
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    isError: false,
  });

  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  useEffect(() => {
    if (!email) {
      setEmailError("");
      setIsEmailValid(false);
    } else if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address");
      setIsEmailValid(false);
    } else {
      setEmailError("");
      setIsEmailValid(true);
    }
  }, [email]);

  const scrollToWaitlist = () => {
    const element = waitlistRef.current;
    if (element) {
      const elementRect = element.getBoundingClientRect();
      const absoluteElementTop = elementRect.top + window.pageYOffset;
      const middleOfElement =
        absoluteElementTop - (window.innerHeight - elementRect.height) / 2;
      window.scrollTo({
        top: middleOfElement,
        behavior: "smooth",
      });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isEmailValid) {
      setSnackbar({
        open: true,
        message: "Please enter a valid email address",
        isError: true,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(
        "https://script.google.com/macros/s/AKfycbwAbuANdndJ86IcWQPdu85nL6P2AdVPio8ouRKLbMkjBMBMegvmyRYb7FhEJNXGq-Weuw/exec",
        {
          method: "POST",
          mode: "no-cors",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email,
            timestamp: new Date().toISOString(),
            source: window.location.href,
          }),
        }
      );

      setSnackbar({
        open: true,
        message: "Thank you for joining our waitlist!",
        isError: false,
      });
      setEmail("");
      setIsEmailValid(false);
    } catch (error) {
      console.error("Submission error:", error);
      setSnackbar({
        open: true,
        message: "Failed to submit. Please try again later.",
        isError: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          position: "relative",
          minHeight: "100vh",
          overflowX: "hidden", // Prevent horizontal scrolling
        }}
      >
        <AnimatedBackground />
        <ResponsiveHeader />
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            pt: 8,
          }}
        >
          <Box
            sx={{
              py: { xs: 8, md: 12 },
              display: "flex",
              alignItems: "center",
              position: "relative",
            }}
          >
            <Container maxWidth="lg">
              <Grid
                container
                rows={{ xs: 12, md: 12 }}
                sx={{
                  alignItems: "center",
                }}
              >
                <Grid item size={{ xs: 12, md: 6 }}>
                  <Box
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      textAlign: { xs: "center", md: "left" },
                    }}
                  >
                    <Typography
                      variant="h3"
                      gutterBottom
                      sx={{
                        fontSize: { xs: "2.5rem", md: "2.5rem" },
                        fontWeight: 700,
                        lineHeight: 1.2,
                        mb: 3,
                      }}
                    >
                      Introducing Langmatiq
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        opacity: 0.9,
                        mb: 6,
                        fontSize: { xs: "1.2rem", md: "1.4rem" },
                        lineHeight: 1.6,
                      }}
                    >
                      Langmatiq is a smart, AI-powered summarizer Chrome extension. Simply choose your language, select any text and get a quick, clear summary right where you are. Perfect for saving time, cutting through clutter, and for readers who prefer fast reading — all completely Free!
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 3,
                        justifyContent: { xs: "center", md: "flex-start" },
                        flexDirection: { xs: "column", sm: "row" },
                      }}
                    >
                      <Button
                        variant="contained"
                        size="large"
                        sx={{
                          minWidth: { xs: "100%", sm: "180px" },
                          fontSize: { xs: "1rem", sm: "1.1rem" },
                        }}
                        onClick={() =>
                          window.open(
                            "https://chromewebstore.google.com/detail/maneejbojpkpmfpnijkjmpkkimifijca?utm_source=item-share-cb",
                            "_blank"
                          )
                        }
                      >
                        Try It Out
                      </Button>
                      {/* <Button
                        variant="contained"
                        size="large"
                        onClick={scrollToWaitlist}
                        sx={{
                          minWidth: { xs: "100%", sm: "180px" },
                          fontSize: { xs: "1rem", sm: "1.1rem" },
                        }}
                      >
                        Join Waitlist
                      </Button> */}
                    </Box>
                  </Box>
                </Grid>
                <Grid
                  item
                  size={{ xs: 12, md: 6 }}
                  sx={{
                    pl: { md: 8 },
                  }}
                >
                  {" "}
                  <Box
                    sx={{
                      display: { xs: "none", md: "flex" },
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                      width: "100%",
                      position: "relative",
                      perspective: "3000px",
                      overflow: "hidden",
                    }}
                  >
                    {/* Main 3D Globe Container */}
                    <Box
                      sx={{
                        width: "600px",
                        height: "600px",
                        position: "relative",
                        transformStyle: "preserve-3d",
                        animation: "rotateGlobe 30s linear infinite",
                        "@keyframes rotateGlobe": {
                          "0%": { transform: "rotateY(0deg)" },
                          "100%": { transform: "rotateY(360deg)" },
                        },
                      }}
                    >
                      {/* Earth Sphere */}
                      <Box
                        sx={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          width: "400px",
                          height: "400px",
                          borderRadius: "50%",
                          background: `url('/earth-texture.png')`,
                          backgroundSize: "cover",
                          boxShadow: `
                            inset -30px -30px 60px rgba(0,0,0,0.6),
                            0 0 80px rgba(0,206,209,0.3)
                          `,
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            borderRadius: "50%",
                            background:
                              "linear-gradient(45deg, transparent 30%, rgba(0,206,209,0.2) 50%, transparent 70%)",
                            animation: "glowEffect 8s linear infinite",
                            "@keyframes glowEffect": {
                              "0%": { transform: "rotate(0deg)" },
                              "100%": { transform: "rotate(360deg)" },
                            },
                          },
                        }}
                      />

                      {/* Latitude Lines */}
                      {[...Array(10)].map((_, i) => (
                        <Box
                          key={`latitude-${i}`}
                          sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            width: "400px",
                            height: "400px",
                            borderRadius: "50%",
                            border: "1px solid rgba(0,206,209,0.2)",
                            transform: `translate(-50%, -50%) rotateX(${
                              i * 20
                            }deg)`,
                          }}
                        />
                      ))}

                      {/* Longitude Lines */}
                      {[...Array(12)].map((_, i) => (
                        <Box
                          key={`longitude-${i}`}
                          sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            width: "400px",
                            height: "400px",
                            borderRadius: "50%",
                            border: "1px solid rgba(0,206,209,0.2)",
                            transform: `translate(-50%, -50%) rotateY(${
                              i * 15
                            }deg)`,
                          }}
                        />
                      ))}

                      {/* Location Markers */}
                      {[...Array(8)].map((_, i) => (
                        <Box
                          key={`marker-${i}`}
                          sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            width: "12px",
                            height: "12px",
                            background: "rgba(0,206,209,0.8)",
                            borderRadius: "50%",
                            transform: `translate(-50%, -50%) rotateY(${
                              i * 45
                            }deg) translateZ(200px)`,
                            "&::after": {
                              content: '""',
                              position: "absolute",
                              width: "100%",
                              height: "100%",
                              borderRadius: "50%",
                              background: "rgba(0,206,209,0.4)",
                              animation: "pulse 2s infinite",
                              "@keyframes pulse": {
                                "0%": {
                                  transform: "scale(1)",
                                  opacity: 0.8,
                                },
                                "100%": {
                                  transform: "scale(4)",
                                  opacity: 0,
                                },
                              },
                            },
                          }}
                        />
                      ))}

                      {/* Connection Lines */}
                      {[...Array(12)].map((_, i) => (
                        <Box
                          key={`connection-${i}`}
                          sx={{
                            position: "absolute",
                            width: "200px",
                            height: "2px",
                            background:
                              "linear-gradient(90deg, transparent, rgba(0,206,209,0.6), transparent)",
                            top: `${30 + Math.random() * 40}%`,
                            left: `${30 + Math.random() * 40}%`,
                            transform: `rotate(${i * 30}deg)`,
                            animation: `glowLine 3s ease-in-out infinite ${
                              i * 0.2
                            }s`,
                            "@keyframes glowLine": {
                              "0%, 100%": { opacity: 0.3 },
                              "50%": { opacity: 0.8 },
                            },
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Container>
          </Box>
          <Box sx={{ backgroundColor: "#0C173A", py: { xs: 8, md: 12 } }}>
            <Container>
              <Typography
                variant="h4"
                gutterBottom
                textAlign="center"
                fontWeight="bold"
                sx={{ mb: 3, fontSize: { xs: "2.5rem", md: "2.5rem" } }}
              >
                More Smart Tools Coming to the Bloomatiq Suite
              </Typography>
              <Typography
                variant="subtitle1"
                textAlign="center"
                sx={{ mb: 8, opacity: 0.9 }}
              >
                Discover our expanding collection of intelligent tools designed
                to enhance your workflow
              </Typography>
              <Grid
                container
                spacing={4}
                columns={{ xs: 12, sm: 12, md: 12 }}
                alignItems="stretch"
              >
                {tools.map((tool) => (
                  <Grid
                    item
                    sx={{ py: 2 }}
                    size={{ xs: 12, sm: 6, md: 4 }}
                    key={tool.title}
                  >
                    <Card
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        p: 3,
                        position: "relative",
                        overflow: "hidden",
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          background:
                            "linear-gradient(45deg, rgba(0,206,209,0.05) 0%, rgba(65,105,225,0.05) 100%)",
                          opacity: 0,
                          transition: "opacity 0.3s ease-in-out",
                        },
                        "&:hover::before": {
                          opacity: 1,
                        },
                      }}
                    >
                      <CardContent
                        sx={{
                          flex: "1 0 auto",
                          position: "relative",
                          zIndex: 1,
                          pb: "0 !important",
                          display: "flex",
                          flexDirection: "column",
                          height: "100%",
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={3} mb={4}>
                          {React.cloneElement(tool.icon, {
                            fontSize: "large",
                            sx: {
                              color: "#00CED1",
                              fontSize: "3rem",
                              transition: "all 0.3s ease-in-out",
                            },
                          })}
                          <Typography
                            variant="h6"
                            fontWeight="700"
                            className="card-title"
                            sx={{
                              fontSize: "1.5rem",
                              transition: "all 0.3s ease-in-out",
                            }}
                          >
                            {tool.title}
                          </Typography>
                        </Box>
                        <Typography
                          variant="body1"
                          sx={{
                            opacity: 0.9,
                            lineHeight: 1.8,
                            fontSize: "1.1rem",
                            color: "rgba(255, 255, 255, 0.85)",
                          }}
                        >
                          {tool.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Container>
          </Box>

          <Box
            sx={{
              py: { xs: 8, md: 10 },
              textAlign: "center",
              backgroundColor: "#0C173A",
              minHeight: { xs: "60vh", md: "auto" }, // Ensure minimum height on mobile
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
            ref={waitlistRef}
          >
            <Container maxWidth="sm">
              <Typography variant="h5" gutterBottom fontWeight="bold">
                Be the First to Experience the Future of Smart Tools
              </Typography>
              <Typography
                variant="subtitle1"
                gutterBottom
                sx={{ opacity: 0.9 }}
              >
                Subscribe to our newsletter and join the waitlist for early
                access to Scanmatiq and the full Bloomatiq Suite.
              </Typography>
              <Box
                component="form"
                noValidate
                autoComplete="off"
                onSubmit={handleSubmit}
                sx={{
                  mt: 4,
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                }}
              >
                <TextField
                  placeholder="Email Address"
                  variant="outlined"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isSubmitting}
                  error={!!emailError}
                  helperText={emailError}
                  InputLabelProps={{
                    shrink: false,
                  }}
                  sx={{
                    width: { xs: "100%", sm: "300px" },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                      backgroundColor: "rgba(255, 255, 255, 0.05)",
                      "& input::placeholder": {
                        color: "rgba(255, 255, 255, 0.7)",
                        opacity: 1,
                      },
                      "& fieldset": {
                        borderColor: "rgba(255, 255, 255, 0.1)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(0, 206, 209, 0.3)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#00CED1",
                      },
                      "&.Mui-error fieldset": {
                        borderColor: "#f44336",
                      },
                    },
                    "& .MuiFormHelperText-root": {
                      position: "absolute",
                      bottom: "-24px",
                      color: "#f44336",
                      marginLeft: 0,
                    },
                  }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting || !isEmailValid}
                  sx={{
                    minWidth: { xs: "100%", sm: "140px" },
                    fontSize: { xs: "1rem", sm: "1.1rem" },
                    mt: { xs: 3, sm: 0 }, // Add margin top on mobile to account for error message
                  }}
                >
                  {isSubmitting ? "Submitting..." : "Submit"}
                </Button>
              </Box>
              <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                message={snackbar.message}
                sx={{
                  "& .MuiSnackbarContent-root": {
                    backgroundColor: snackbar.isError ? "#f44336" : "#43a047",
                    color: "#fff",
                  },
                }}
              />
            </Container>
          </Box>

          <Box
            sx={{
              backgroundColor: "#0C173A",
              py: 6,
              borderTop: "1px solid rgba(0, 206, 209, 0.1)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <FooterBackground />
            <Container maxWidth="lg">
              <Box
                sx={{ textAlign: "center", position: "relative", zIndex: 1 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 4,
                  }}
                >
                  <img src={Logo} alt="Bloomatiq" style={{ height: 48 }} />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    gap: 3,
                    justifyContent: "center",
                    mb: 4,
                  }}
                >
                  <IconButton
                    component="a"
                    href="https://www.facebook.com/Bloomatiq/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Visit Bloomatiq on Facebook"
                  >
                    <FacebookIcon sx={{ fontSize: "1.75rem" }} />
                  </IconButton>
                  <IconButton
                    component="a"
                    href="https://www.instagram.com/bloomatiq"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Visit Bloomatiq on Instagram"
                  >
                    <InstagramIcon sx={{ fontSize: "1.75rem" }} />
                  </IconButton>
                  <IconButton
                    component="a"
                    href="https://www.linkedin.com/company/Bloomatiq"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Visit Bloomatiq on LinkedIn"
                  >
                    <LinkedInIcon sx={{ fontSize: "1.75rem" }} />
                  </IconButton>
                  <IconButton
                    component="a"
                    href="https://x.com/_bloomatiq"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Visit Bloomatiq on X (Twitter)"
                  >
                    <XIcon sx={{ fontSize: "1.75rem" }} />
                  </IconButton>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    opacity: 0.8,
                    position: "relative",
                    zIndex: 1,
                    fontSize: "1rem",
                  }}
                >
                  © 2025 Bloomatiq. All rights reserved.
                </Typography>
              </Box>
            </Container>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
